import { useEffect, useState } from "react";
import { drenarAvisos, lerAvisosPendentes, type AvisosPendentes } from "../lib/faculdade";

// A fila de avisos ao aluno — visível no balcão da secretaria.
//
// Pedido da reunião de 31/08: *"avisar o aluno por e-mail quando um documento dele
// chegar e ficar disponível pra baixar na secretaria"*.
//
// ---------------------------------------------------------------------------
// POR QUE ESTA PEÇA APARECE NA TELA, e não é só um cron silencioso
// ---------------------------------------------------------------------------
// O hub **não tem provedor de e-mail transacional**. Os 13 templates de
// `supabase/emails/` são do Auth, disparados pelo próprio Supabase — servem para
// convite e recuperação de senha, não para e-mail arbitrário. Então enquanto a
// chave não existe, os avisos acumulam na fila (000145).
//
// Fila invisível é como dado some sem ninguém ver — a lição do relatório de carga
// onde nenhuma exclusão é silenciosa. Esta peça mostra o número, e quando ele é
// zero ela desaparece: zero grande é ruído, e a doutrina do tile de fato é a
// mesma ("carteira vazia, painel sem fato").
//
// ---------------------------------------------------------------------------
// O botão diz a VERDADE quando não há como enviar
// ---------------------------------------------------------------------------
// A Edge Function responde **503 com a contagem** sem provedor configurado, e a
// tela repassa esse texto. Dizer "enviei 0" seria o silêncio que esta casa
// recusa — e o operador ficaria esperando que os alunos recebessem.

export default function FilaDeAvisos() {
  const [fila, setFila] = useState<AvisosPendentes[]>([]);
  const [recado, setRecado] = useState<{ tom: "ok" | "erro"; texto: string } | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function carregar() {
    try {
      setFila(await lerAvisosPendentes());
    } catch {
      // Erro de LEITURA desta peça não pode derrubar o balcão: a fila de
      // documentos é o trabalho, e o aviso é acessório. A peça que falha é que
      // informa, não a tela toda — a lição do Calendário, onde um tique que não
      // gravou trocava o mês inteiro pela mensagem de erro.
      setFila([]);
    }
  }

  useEffect(() => {
    void carregar();
  }, []);

  const pendentes = fila.reduce((s, f) => s + Number(f.pendentes ?? 0), 0);
  const comErro = fila.reduce((s, f) => s + Number(f.com_erro ?? 0), 0);

  // Zero pendentes: a peça sai da tela.
  if (pendentes === 0 && !recado) return null;

  async function enviar() {
    setEnviando(true);
    setRecado(null);
    const r = await drenarAvisos();
    setEnviando(false);
    if (r.ok) {
      setRecado({
        tom: r.falharam > 0 ? "erro" : "ok",
        texto:
          `${r.enviados} aviso(s) enviado(s)` +
          (r.falharam > 0 ? `, ${r.falharam} falharam e continuam na fila` : "") +
          (r.pendentes > 0 ? ` · ${r.pendentes} ainda esperando` : ""),
      });
    } else {
      setRecado({
        tom: "erro",
        texto: r.erro + (r.pendentes !== null ? ` (${r.pendentes} na fila)` : ""),
      });
    }
    await carregar();
  }

  return (
    <div
      data-fila-avisos={pendentes}
      className="rounded-xl border border-aviso-borda bg-aviso-fundo p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-aviso-tinta">
            {pendentes} aviso{pendentes === 1 ? "" : "s"} esperando envio
          </h3>
          <p className="mt-0.5 text-sm text-aviso-tinta/85">
            Alunos com documento pronto que ainda não foram avisados por e-mail
            {comErro > 0 ? ` · ${comErro} com erro na última tentativa` : ""}
            {fila[0]?.mais_antigo
              ? ` · o mais antigo é de ${new Date(fila[0].mais_antigo).toLocaleDateString("pt-BR")}`
              : ""}
            .
          </p>
        </div>
        <button
          type="button"
          data-enviar-avisos
          onClick={enviar}
          disabled={enviando || pendentes === 0}
          className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
        >
          {enviando ? "Enviando…" : "Enviar agora"}
        </button>
      </div>

      {recado && (
        <p
          className={`mt-3 rounded-lg p-3 text-sm ${
            recado.tom === "ok"
              ? "border border-aviso-borda bg-aviso-fundo text-sucesso"
              : "border border-erro-borda bg-erro-fundo text-erro-tinta"
          }`}
        >
          {recado.texto}
        </p>
      )}
    </div>
  );
}
